import {
  Component,
  ElementRef,
  Input,
  NgZone,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  Chart,
  ChartConfiguration,
  ChartData,
  Filler,
  LineElement,
  PointElement,
  RadarController,
  RadialLinearScale
} from 'chart.js';
import { UtilityService } from 'src/app/_services/utility.service';

export interface Competence {
  alias: string;
  acronym: string;
  is_highlighted: boolean;
  auto_evaluation: number;
  review_evaluation: number;
  img_path: string;
  description: string;
}

@Component({
  selector: 'app-chart-transversal',
  templateUrl: './chart-transversal.component.html',
  styleUrls: ['./chart-transversal.component.css']
})
export class ChartTransversalComponent {
  @Input() competences: Competence[] = [];
  @Input() isSelfevaluation: boolean = false;
  @Input() height: string;
  @Input() iconDimension: number; //dimensione delle icone in px

  @ViewChild('myRadarChart') myRadarChart!: ElementRef<HTMLCanvasElement>;
  radarChart: Chart | null = null;

  // Mappa degli emoji per gli alias
  emojiMap: { [key: string]: string } = {
    AC: '🔄',
    AA: '🕵️‍♂️',
    AD: '🔍',
    CN: '🤝',
    CE: '💬',
    CT: '🤖',
    GS: '🧘‍♀️',
    LC: '👥',
    MA: '🧪',
    MO: '🌍',
    OT: '📋',
    OO: '🎯',
    PC: '🎨',
    PS: '🧠',
    VS: '🧭'
  };

  constructor(
    public readonly utilityService: UtilityService,
    private readonly ngZone: NgZone
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['competences']) {
      this.createGraphic();
    }
    if (changes['isSelfevaluation']) {
      this.createGraphic();
    }
  }

  ngAfterViewInit() {
    if (this.competences.length > 0) {
      this.createGraphic();
    }
  }

  createGraphic() {
    if (!this.myRadarChart) return;
    this.ngZone.runOutsideAngular(() => {
      if (this.radarChart) {
        this.radarChart.destroy();
        this.radarChart = null;
      }

      // Estrarre gli acronym (alias) e nomi filtrati
      const fixedAcronym = this.competences.map(
        (skill: any) => skill.acronym ?? '-'
      ); // Usa gli acronym come labels

      // Inizializza i valori con 0
      const votesSelfEvaluation = Array(fixedAcronym.length).fill(0);
      const voteFeedback360 = Array(fixedAcronym.length).fill(0);

      this.competences.forEach((skill: any) => {
        const index = fixedAcronym.indexOf(skill.acronym); // Trova la posizione usando l'acronym
        if (index !== -1) {
          votesSelfEvaluation[index] = skill.auto_evaluation ?? 0; // Imposta il valore della media
          voteFeedback360[index] = skill.review_evaluation ?? 0; // Imposta il valore della media
        }
      });

      const fixedAliasesEmoji = this.competences.map((skill: any) => {
        const acronym = skill.acronym ?? '-';
        return this.emojiMap[acronym] ?? acronym; // Se non c'è l'emoji, usa l'acronym
      });

      const fixedAliases = this.competences.map((skill: any) => {
        const alias = skill.alias ?? '-';
        return alias;
      });

      const pointColorsAuto = this.competences.map((skill: any) =>
        skill.is_highlighted ? 'rgb(255, 150, 51)' : 'rgb(54, 162, 235)'
      );
      const pointHoverColorsAuto = this.competences.map((skill: any) => '#fff');

      const pointColorsFeedback = this.competences.map((skill: any) =>
        skill.is_highlighted ? 'rgb(255, 150, 51)' : 'rgb(254, 100, 132)'
      );
      const pointHoverColorsFeedback = this.competences.map(
        (skill: any) => '#fff'
      );

      // Configurazione del grafico
      Chart.register(
        RadarController,
        RadialLinearScale,
        PointElement,
        LineElement,
        Filler
      );
      const data: ChartData<'radar'> = {
        labels: fixedAliasesEmoji, // Mostra solo gli acronym filtrati come etichette
        datasets: [
          {
            label: 'Feedback 360',
            data: voteFeedback360,
            fill: true,
            backgroundColor: 'rgba(255, 179, 194, 0.5)',
            borderColor: 'rgb(254, 100, 132)',
            pointRadius: 5,
            pointHoverRadius: 5,
            pointBackgroundColor: !this.isSelfevaluation
              ? pointColorsFeedback
              : 'rgb(254, 100, 132)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: pointHoverColorsFeedback,
            pointHoverBorderColor: 'rgb(254, 100, 132)'
          },
          {
            label: 'Autovalutazione',
            data: votesSelfEvaluation,
            fill: true,
            backgroundColor: 'rgba(54, 162, 235, 0.5)',
            borderColor: 'rgb(54, 162, 235)',
            pointRadius: 5,
            pointHoverRadius: 5,
            pointBackgroundColor: this.isSelfevaluation
              ? pointColorsAuto
              : 'rgb(54, 162, 235)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: pointHoverColorsAuto,
            pointHoverBorderColor: 'rgb(54, 162, 235)'
          }
        ]
      };

      const config: ChartConfiguration<'radar'> = {
        type: 'radar',
        data: data,
        options: {
          elements: {
            line: {
              borderWidth: 2
            }
          },
          scales: {
            r: {
              suggestedMin: 1,
              suggestedMax: 10,
              angleLines: {
                display: false
              },
              pointLabels: {
                font: {
                  size: this.iconDimension
                }
              }
            }
          },
          plugins: {
            tooltip: {
              callbacks: {
                label: context => {
                  const index = context.dataIndex;
                  const value = context.raw;
                  const alias = fixedAliases[index] ?? 'Ciao';
                  return `${alias}: ${value}`;
                }
              }
            }
          }
        }
      };

      // Creazione del grafico
      const ctx = this.myRadarChart.nativeElement.getContext('2d');
      this.radarChart = new Chart(ctx!, config);
    });
  }
}
